import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './hooks/useAuth';
import OAuthCallback from './pages/OAuthCallback';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import NotFound from './pages/NotFound';
import './App.css';
import './utils/chartSetup'; // Eagerly register Chart.js before any lazy chunk loads

// Lazy-loaded pages (code-split for faster initial load)
const StudentDashboard = lazy(() => import('./dashboards/StudentDashboard'));
const TeacherDashboard = lazy(() => import('./dashboards/TeacherDashboard'));
const AdminDashboard = lazy(() => import('./dashboards/AdminDashboard'));
const ClassDetail = lazy(() => import('./pages/ClassDetail'));
const ClassList = lazy(() => import('./pages/ClassList'));
const StudentProfile = lazy(() => import('./pages/StudentProfile'));
const TeacherProfile = lazy(() => import('./pages/TeacherProfile'));
const AdminProfile = lazy(() => import('./pages/AdminProfile'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Store = lazy(() => import('./pages/Store'));
const Cart = lazy(() => import('./pages/Cart'));
const Stocks = lazy(() => import('./pages/Stocks'));
const Gallery = lazy(() => import('./pages/Gallery'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Results = lazy(() => import('./pages/Results'));
const OurCourses = lazy(() => import('./pages/OurCourses'));
const GrievancePolicy = lazy(() => import('./pages/GrievancePolicy'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const Notices = lazy(() => import('./pages/Notices'));
const AkshayInchargeDashboard = lazy(() => import('./pages/AkshayInchargeDashboard'));
const CanteenInchargeDashboard = lazy(() => import('./pages/CanteenInchargeDashboard'));
const CanteenPage = lazy(() => import('./pages/CanteenPage'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div className="spinner" style={{ width: 40, height: 40, border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Navbar />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/store" element={<Store />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/stocks" element={
            <PrivateRoute requiredRole="admin">
              <Stocks />
            </PrivateRoute>
          } />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/results" element={<Results />} />
          <Route path="/our-courses" element={<OurCourses />} />
          <Route path="/grievance-policy" element={<GrievancePolicy />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/notices" element={<Notices />} />
          <Route path="/akshay-incharge"  element={<AkshayInchargeDashboard />} />
          <Route path="/canteen-incharge" element={<CanteenInchargeDashboard />} />
          <Route path="/canteen" element={<CanteenPage />} />
          
          <Route 
            path="/student-dashboard" 
            element={
              <PrivateRoute requiredRole="student">
                <StudentDashboard />
              </PrivateRoute>
            } 
          />

          <Route 
            path="/student-profile" 
            element={
              <PrivateRoute requiredRole="student">
                <StudentProfile />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/teacher-dashboard" 
            element={
              <PrivateRoute requiredRole="teacher">
                <TeacherDashboard />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/teacher-profile" 
            element={
              <PrivateRoute requiredRole="teacher">
                <TeacherProfile />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/admin-dashboard" 
            element={
              <PrivateRoute requiredRole="admin">
                <AdminDashboard />
              </PrivateRoute>
            } 
          />

          <Route 
            path="/admin-profile" 
            element={
              <PrivateRoute requiredRole="admin">
                <AdminProfile />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/class/:classId" 
            element={
              <PrivateRoute>
                <ClassDetail />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/classes" 
            element={
              <PrivateRoute>
                <ClassList />
              </PrivateRoute>
            } 
          />

          {/* OAuth callback — receives token+role from backend and redirects to correct dashboard */}
          <Route path="/dashboard" element={<OAuthCallback />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
